<template>
  <div>
    <CreateAcquirerAffiliationModal
      v-if="showCreateModal"
      :acquirer="consentData.acquirer"
      :loading="redirectAffiliationLoading"
      @confirm="redirectAffiliation"
      @close="() => (showCreateModal = false)"
    />

    <div class="flex flex-col gap-2">
      <ConfirmAcquirerAffiliationAlert v-if="showConfirmAlert" />
      <UpdateAcquirerAffiliationAlert v-if="showUpdateAlert" />
      <ErrorAcquirerAffiliationAlert v-if="showErrorAlert" />
      <SuccessAcquirerAffiliationAlert
        v-if="showSuccessAlert"
        :acquirer="consentData.acquirer"
      />
    </div>

    <ConfigBox
      ref="acquirerConfigBox"
      :loading="showLoading"
      :as="asDivInsteadOfFieldSet"
      :callback="saveAcquirerAffiliation"
      :title="$t('acquirers')"
      :hideEditButton="!canEdit"
      hiddenAlert
      @cancelEdit="handleCancelEdit"
    >
      <template #content>
        <div class="flex flex-col gap-4">
          <template v-if="showCreate">
            <CreateAffiliationItem
              v-for="(affiliation, index) in availableAcquirerAffiliations"
              :acquirer="affiliation"
              :key="affiliation.acquirer + index"
              :loading="redirectAffiliationLoading"
              :canCreate="canCreate"
              @handleCreate="handleCreateAffiliation"
            />
          </template>

          <AffiliationItem
            v-if="consentData.acquirer?.affiliation_code"
            :acquirer="consentData.acquirer"
          />

          <AffiliationItem
            v-for="(affiliation, index) in acquirerAffiliations"
            :acquirer="affiliation"
            :key="affiliation.acquirer + index"
            @handleChange="setStatusByAcquierer"
          />

          <jade-button
            v-if="hasAffiliationsCreated"
            class="max-w-56 self-end mt-2"
            variant="neutral-subtle"
            size="medium"
            icon="plus"
            @click="handleCreateAffiliation"
          >
            {{ $t('affiliation-add') }}
          </jade-button>
        </div>
      </template>
    </ConfigBox>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ConfigBox from '@components/project/molecules/ConfigBox.vue'
import { JadeButton } from '@jade'
import { wait } from '@utils/wait.utils'
import { structuredClone } from '@utils/structured-clone.utils'
import {
  affiliationSettingsEditEnabled,
  affiliationSettingsCreateEnabled
} from '@/actions-permission/affiliations-settings.permissions'
import {
  ACQUIRER_BY_CONSENT_ENUM,
  AFFILIATIONS_PAYMENT_METHODS_ENUM,
  getAffiliationsByEnv
} from './affiliations-payment-methods.enum'

const TIME_CLOSE_ALERT = 5000

export default {
  name: 'SettingsAffiliations',

  components: {
    ConfigBox,
    JadeButton,
    CreateAffiliationItem: () =>
      import('./components/CreateAffiliationItem.vue'),
    AffiliationItem: () => import('./components/AffiliationItem.vue'),
    CreateAcquirerAffiliationModal: () =>
      import('./components/CreateAcquirerAffiliationModal.vue'),
    ConfirmAcquirerAffiliationAlert: () =>
      import('./components/ConfirmAcquirerAffiliationAlert.vue'),
    SuccessAcquirerAffiliationAlert: () =>
      import('./components/SuccessAcquirerAffiliationAlert.vue'),
    ErrorAcquirerAffiliationAlert: () =>
      import('./components/ErrorAcquirerAffiliationAlert.vue'),
    UpdateAcquirerAffiliationAlert: () =>
      import('./components/UpdateAcquirerAffiliationAlert.vue')
  },

  data () {
    return {
      isLoading: false,
      redirectAffiliationLoading: false,
      showCreateModal: false,
      showSuccessAlert: false,
      showErrorAlert: false,
      showUpdateAlert: false,
      showConfirmAlert: false,
      hasAffiliationsCreated: false,
      acquirerAffiliations: [],
      availableAcquirerAffiliations: [],
      consentData: {
        code: '',
        state: '',
        acquirer: {
          payment_method: '',
          affiliation_code: ''
        }
      }
    }
  },

  computed: {
    ...mapGetters([
      'permissionIsEnabled',
      'affiliationsLoading',
      'merchantId',
      'accountId'
    ]),

    showLoading () {
      return this.isLoading || this.affiliationsLoading
    },

    showCreate () {
      return !this.hasAffiliationsCreated && !this.hasAffliationCodeAndState
    },

    canCreate () {
      return affiliationSettingsCreateEnabled(this.permissionIsEnabled)
    },

    canEdit () {
      return (
        this.hasAffiliationsCreated ||
        (this.hasAffliationCodeAndState &&
          !this.affiliationsLoading &&
          affiliationSettingsEditEnabled(this.permissionIsEnabled))
      )
    },

    hasAffliationCodeAndState () {
      return !!this.consentData.code && !!this.consentData.state
    },

    asDivInsteadOfFieldSet () {
      return !this.hasAffiliationsCreated ? 'div' : 'fieldset'
    }
  },

  async created () {
    const { code, state } = this.$route.query
    this.consentData = {
      code,
      state
    }

    if (this.hasAffliationCodeAndState) {
      this.showConfirmAlert = true
      this.consentData.acquirer = {
        payment_method: this.consentData.state,
        affiliation_code: this.consentData.code.replace(
          /(.{4}).*(.{4})/,
          '$1*****$2'
        )
      }
    }

    await this.getAffiliations()
  },

  mounted () {
    if (this.hasAffliationCodeAndState) {
      this.$refs.acquirerConfigBox.handleClickEditBtn()
    }
  },

  methods: {
    ...mapActions({
      updateAcquirerAffiliation: 'UPDATE_ACQUIRER_AFFILIATION',
      confirmAcquirerConsent: 'CONFIRM_ACQUIRER_CONSENT',
      getAcquirerAffiliations: 'GET_ACQUIRER_AFFILIATIONS',
      createAcquirerConsent: 'CREATE_ACQUIRER_CONSENT'
    }),

    removeAffiliationInfo () {
      this.$router.replace({ query: null })
    },

    handleCancelEdit () {
      this.removeAffiliationInfo()
      this.resetData()
    },

    setStatusByAcquierer (acquirer) {
      this.acquirerAffiliations.forEach(affiliation => {
        if (
          affiliation.merchant_acquirer_agreement_id ===
          acquirer.merchant_acquirer_agreement_id
        ) {
          affiliation.is_enabled = acquirer.is_enabled
        }
      })
    },

    handleCreateAffiliation (acquirer) {
      this.showCreateModal = true
      this.consentData.acquirer = acquirer?.payment_method
        ? acquirer
        : {
            payment_method: AFFILIATIONS_PAYMENT_METHODS_ENUM.PAG_SEGURO_STAGING
          }
    },

    async getAffiliations () {
      const affiliationsByEnv = getAffiliationsByEnv()
      const affiliations =
        structuredClone(await this.getAcquirerAffiliations()) ?? []

      this.acquirerAffiliations = affiliations.filter(affiliation =>
        affiliationsByEnv.includes(affiliation.payment_method)
      )
      this.hasAffiliationsCreated = this.acquirerAffiliations.length > 0

      if (!this.acquirerAffiliations.length) {
        this.hasAffiliationsCreated = false
        this.availableAcquirerAffiliations = affiliationsByEnv.map(
          acquirer => ({
            payment_method: acquirer,
            acquirer
          })
        )
      }
    },

    async resetData () {
      this.showConfirmAlert = false
      this.showSuccessAlert = false
      this.showErrorAlert = false
      this.showUpdateAlert = false

      this.consentData = {
        code: '',
        state: '',
        acquirer: {
          payment_method: '',
          affiliation_code: ''
        }
      }

      this.$refs.acquirerConfigBox.cancelEdit()
    },

    async redirectAffiliation () {
      this.redirectAffiliationLoading = true
      const acquirer = this.consentData.acquirer.payment_method
      try {
        const body = {
          acquirer: [ACQUIRER_BY_CONSENT_ENUM[acquirer]]
        }
        const result = await this.createAcquirerConsent({
          body
        })

        window.location.href = result[ACQUIRER_BY_CONSENT_ENUM[acquirer]]
      } catch (error) {
        this.showErrorAlert = true
        setTimeout(() => (this.showErrorAlert = false), TIME_CLOSE_ALERT)
      } finally {
        this.redirectAffiliationLoading = false
        this.showCreateModal = false
      }
    },

    async createAffiliation (password) {
      this.isLoading = true
      try {
        const { code, state, acquirer } = this.consentData

        const body = {
          [ACQUIRER_BY_CONSENT_ENUM[acquirer.payment_method]]: {
            code,
            state
          },
          password
        }

        await this.confirmAcquirerConsent({ body })

        this.showSuccessAlert = true
        this.removeAffiliationInfo()
      } catch (error) {
        this.showErrorAlert = true
      } finally {
        this.showConfirmAlert = false
        this.isLoading = false
      }
    },

    async updateStatusAcquierer (password) {
      this.isLoading = true

      try {
        await Promise.all(
          this.acquirerAffiliations.map(async affiliation => {
            const payload = {
              affiliationId: affiliation.merchant_acquirer_agreement_id,
              body: {
                pag_seguro: {
                  enabled: affiliation.is_enabled
                },
                password
              }
            }

            return this.updateAcquirerAffiliation(payload)
          })
        )

        this.showUpdateAlert = true
      } catch (error) {
        this.showErrorAlert = true
      } finally {
        this.isLoading = false
      }
    },

    async saveAcquirerAffiliation (password) {
      if (this.hasAffliationCodeAndState) {
        await this.createAffiliation(password)
      } else {
        await this.updateStatusAcquierer(password)
      }

      await wait(TIME_CLOSE_ALERT)

      await this.resetData()

      await this.getAffiliations()
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "acquirers": "Adquirentes",
      "affiliation-add": "Cadastrar nova afiliação"
    },
    "en-US": {
      "acquirers": "Acquirers",
      "affiliation-add": "Create new affiliation"
    }
  }
</i18n>
